<script setup lang="ts">
import { Menu, ChevronDown } from 'lucide-vue-next';
import SearchModal from '~/components/base/SearchModel.vue';
import { buttonVariants } from '~/components/ui/button';
import type { Page } from '#shared/types/schema';
import { withLeadingSlash, withoutTrailingSlash } from 'ufo';

const { isAuthenticated } = useAuth();

interface NavigationItem {
	id: string;
	title: string;
	url?: string;
	page?: { permalink: string };
	children?: NavigationItem[];
	style?: 'default' | 'button' | null;
}

// Using template ref to expose the navigation bar to the layout for visual editing
const navigationRef = useTemplateRef('navigationRef');
defineExpose({ navigationRef });

interface Navigation {
	id: string;
	items: NavigationItem[];
}

interface Globals {
	logo?: string;
	logo_dark_mode?: string;
}

const props = defineProps<{
	navigation: Navigation;
	globals: Globals;
}>();

const menuOpen = ref(false);
const runtimeConfig = useRuntimeConfig();
const { setAttr } = useVisualEditing();
const route = useRoute();

// Recupera la pagina corrente per controllare main_navigation_style
const permalink = computed(() => withoutTrailingSlash(withLeadingSlash(route.path)));

// Escludi le route di autenticazione dal fetch
const authRoutes = ['/login', '/forgot-password', '/reset-password', '/area-riservata'];
const currentPath = computed(() => withoutTrailingSlash(withLeadingSlash(route.path)));
const isAuthRoute = computed(() => authRoutes.some(authRoute => currentPath.value.startsWith(authRoute)));

// Fai il fetch solo se non è una route di autenticazione
// IMPORTANTE: Per le route di autenticazione, usiamo un URL speciale '/skip'
// che l'API gestirà restituendo null invece di un 404
const shouldFetchPage = computed(() => !isAuthRoute.value);
const fetchUrl = computed(() => {
	if (!shouldFetchPage.value) {
		// Usa un URL speciale che l'API riconoscerà e gestirà senza errori
		return '/api/pages/one?permalink=/skip';
	}
	return `/api/pages/one?permalink=${encodeURIComponent(currentPath.value)}`;
});

const { data: currentPage, refresh, status } = await useFetch<Page>(
	fetchUrl,
	{
		lazy: true,
		server: false, // Esegui solo lato client per evitare fetch durante SSR
		// Non lanciare errori se la pagina non esiste (potrebbe essere una route diversa)
		onResponseError: () => {},
		default: () => null as any,
		// Usa il permalink come key per forzare il refetch quando cambia
		key: `page-${currentPath.value}`,
		// Non fare il fetch automaticamente - solo quando chiamiamo refresh()
		immediate: false,
	},
);

// Stato per tracciare se stiamo ancora caricando i dati iniziali
// La navbar viene nascosta durante il caricamento iniziale per evitare il flash bianco
// IMPORTANTE: Questo stato viene resettato solo al primo caricamento, non ad ogni cambio di route
const isInitialLoad = useState('navbarInitialLoad', () => !isAuthRoute.value);
const hasInitialLoadCompleted = useState('navbarInitialLoadCompleted', () => false);

// Watch per forzare il refresh quando cambia la route (solo per il fetch)
watch(currentPath, (newPath) => {
	// Verifica se la nuova route è una route di autenticazione
	const isNewPathAuthRoute = authRoutes.some(authRoute => newPath.startsWith(authRoute));
	
	// IMPORTANTE: Non resettare isInitialLoad ad ogni cambio di route
	// La navbar deve rimanere visibile durante le navigazioni successive
	// Solo al primo caricamento (SSR/initial mount) nascondiamo la navbar
	if (isNewPathAuthRoute) {
		// Per le route auth, non c'è caricamento iniziale
		if (!hasInitialLoadCompleted.value) {
			isInitialLoad.value = false;
			hasInitialLoadCompleted.value = true;
		}
		// IMPORTANTE: Non chiamare refresh() per le route di autenticazione
		// Questo previene chiamate accidentali all'API pages/one
		return;
	}
	
	// Solo per route non-auth, fai il refresh
	if (process.client && shouldFetchPage.value) {
		// Forza il refresh del fetch quando cambia la route (solo per route non-auth)
		refresh();
	}
}, { immediate: true });

// Watch per disattivare il caricamento iniziale quando il fetch è completato
// Questo viene eseguito solo al primo caricamento
watch(status, (newStatus) => {
	if ((newStatus === 'success' || newStatus === 'error') && !hasInitialLoadCompleted.value) {
		// Il fetch è completato (con successo o errore), possiamo mostrare la navbar
		// Questo avviene solo al primo caricamento
		isInitialLoad.value = false;
		hasInitialLoadCompleted.value = true;
	}
}, { immediate: true });

const lightLogoUrl = computed(() => {
	if (!props.globals?.logo || !runtimeConfig.public.directusUrl) {
		return '/images/logo.svg';
	}
	return `${runtimeConfig.public.directusUrl}/assets/${props.globals.logo}`;
});

const darkLogoUrl = computed(() => {
	if (!props.globals?.logo_dark_mode || !runtimeConfig.public.directusUrl) {
		return '';
	}
	return `${runtimeConfig.public.directusUrl}/assets/${props.globals.logo_dark_mode}`;
});

// Determina se la pagina è configurata come trasparente (senza considerare lo scroll)
// IMPORTANTE: Per le route di autenticazione, sempre non trasparente, senza fare fetch
const pageHasTransparentNav = computed(() => {
	// Le route di autenticazione non devono mai essere trasparenti
	// Non facciamo mai fetch per queste route, quindi restituiamo sempre false
	if (isAuthRoute.value) {
		return false;
	}
	// Se non abbiamo ancora caricato la pagina, non è trasparente
	if (!currentPage.value) {
		return false;
	}
	// Controlla se la pagina ha main_navigation_style === 'transparent'
	return (currentPage.value as Page | null)?.main_navigation_style === 'transparent';
});

// Stato per tracciare se si è scrollato oltre il primo blocco
const hasScrolledPastFirstBlock = ref(false);

// Variabile per tracciare l'ultima posizione di scroll (per isteresi)
let lastScrollY = 0;

// Funzione per controllare se si è superato il primo blocco
const checkScrollPosition = () => {
	if (!process.client || !pageHasTransparentNav.value) {
		hasScrolledPastFirstBlock.value = false;
		return;
	}

	// Cerca il primo blocco della pagina
	const firstBlock = document.querySelector('[data-background]');
	if (!firstBlock) {
		hasScrolledPastFirstBlock.value = false;
		return;
	}

	// Ottieni l'altezza della navbar per i calcoli
	const navbarHeight = navigationRef.value?.offsetHeight || 80;
	
	// Usa getBoundingClientRect per ottenere la posizione relativa al viewport
	const blockRect = firstBlock.getBoundingClientRect();
	const scrollY = window.scrollY || window.pageYOffset;
	
	// Calcola la posizione assoluta del blocco
	// Quando la navbar è absolute, il blocco parte da top: 0 (senza padding)
	// Quando la navbar è fixed, il blocco ha padding-top, quindi la posizione cambia
	// Per calcolare correttamente, dobbiamo considerare la posizione attuale del blocco
	const blockTop = blockRect.top + scrollY;
	const blockHeight = blockRect.height;
	const blockBottom = blockTop + blockHeight;
	
	// La soglia deve essere quando il TOP del blocco esce completamente dal viewport
	// (non la fine del blocco, ma quando il blocco è completamente sopra il viewport)
	// Consideriamo che quando la navbar è fixed, occupa spazio in alto
	const viewportTop = scrollY;
	const viewportBottom = scrollY + window.innerHeight;
	
	// Il blocco è completamente sopra il viewport quando blockBottom < viewportTop
	// Ma vogliamo attivare quando il blocco è quasi completamente fuori dal viewport
	// Soglia con isteresi per evitare oscillazioni:
	const scrollingDown = scrollY > lastScrollY;
	
	// Quando scrolli verso il basso: attiva quando il blocco è completamente sopra il viewport (con margine)
	// Quando scrolli verso l'alto: disattiva quando il blocco torna visibile nel viewport
	const thresholdDown = blockBottom - navbarHeight; // Quando il blocco finisce sopra la navbar fixed
	const thresholdUp = blockTop - navbarHeight + 50; // Quando il blocco torna visibile (con margine)
	
	if (scrollingDown && scrollY >= thresholdDown) {
		// Scrolling verso il basso: attiva fixed quando il blocco è completamente sopra
		hasScrolledPastFirstBlock.value = true;
	} else if (!scrollingDown && scrollY <= thresholdUp) {
		// Scrolling verso l'alto: disattiva fixed quando il blocco torna visibile
		hasScrolledPastFirstBlock.value = false;
	}
	// Altrimenti mantieni lo stato corrente (isteresi)
	
	lastScrollY = scrollY;
};

// Wrapper per requestAnimationFrame per ottimizzare le performance
let rafId: number | null = null;
const throttledCheckScrollPosition = () => {
	if (rafId !== null) {
		cancelAnimationFrame(rafId);
	}
	rafId = requestAnimationFrame(() => {
		checkScrollPosition();
		rafId = null;
	});
};

// Determina se la navbar deve essere trasparente (considerando anche lo scroll e il menu mobile)
// La navbar è trasparente solo se:
// 1. La pagina è configurata come trasparente
// 2. NON abbiamo scrollato oltre il primo blocco
// 3. Il menu mobile NON è aperto (su mobile, quando il menu è aperto, la navbar diventa bianca)
const isTransparentNav = computed(() => {
	// Se il menu mobile è aperto, la navbar non deve essere trasparente
	if (menuOpen.value) {
		return false;
	}
	return pageHasTransparentNav.value && !hasScrolledPastFirstBlock.value;
});

// Condividi lo stato della navbar trasparente con il layout tramite useState
const navbarTransparentState = useState('navbarTransparent', () => false);
watch(isTransparentNav, (value) => {
	navbarTransparentState.value = value;
}, { immediate: true });

// Condividi lo stato del menu mobile aperto con il layout per evitare padding-top quando è aperto
const menuOpenState = useState('menuOpen', () => false);
watch(menuOpen, (value) => {
	menuOpenState.value = value;
}, { immediate: true });

// Condividi lo stato dello scroll per permettere al PageBuilder di aggiungere padding
const hasScrolledPastFirstBlockState = useState('hasScrolledPastFirstBlock', () => false);
watch(hasScrolledPastFirstBlock, (newValue, oldValue) => {
	// Quando lo stato cambia, compensa la posizione di scroll per evitare il "salto"
	if (process.client && newValue !== oldValue && pageHasTransparentNav.value) {
		// Salva la posizione corrente PRIMA che il DOM cambi
		const currentScrollY = window.scrollY || window.pageYOffset;
		const navbarHeight = navigationRef.value?.offsetHeight || 80;
		
		// Aggiorna lo stato condiviso
		hasScrolledPastFirstBlockState.value = newValue;
		
		// Aspetta che il DOM si aggiorni (il padding-top viene aggiunto/rimosso)
		nextTick(() => {
			// Usa doppio requestAnimationFrame per assicurarsi che il DOM sia completamente aggiornato
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					// Quando la navbar diventa fixed (newValue = true), il padding-top viene aggiunto
					// quindi il contenuto si sposta verso il basso. Dobbiamo aggiungere l'altezza del padding
					// alla posizione di scroll per mantenere la stessa posizione visiva.
					// Quando la navbar torna absolute (newValue = false), il padding-top viene rimosso
					// quindi dobbiamo sottrarre l'altezza del padding.
					const adjustedScrollY = newValue 
						? currentScrollY + navbarHeight  // Aggiungi padding quando diventa fixed
						: Math.max(0, currentScrollY - navbarHeight); // Rimuovi padding quando torna absolute
					
					window.scrollTo({
						top: adjustedScrollY,
						behavior: 'auto' as ScrollBehavior,
					});
				});
			});
		});
	} else {
		hasScrolledPastFirstBlockState.value = newValue;
	}
}, { immediate: true });

// Setup del listener di scroll quando la pagina è trasparente
watch([pageHasTransparentNav, currentPath], () => {
	if (!process.client) return;
	
	// Rimuovi sempre il listener esistente
	window.removeEventListener('scroll', throttledCheckScrollPosition);
	if (rafId !== null) {
		cancelAnimationFrame(rafId);
		rafId = null;
	}
	
	// Reset dello stato quando cambia la route
	hasScrolledPastFirstBlock.value = false;
	lastScrollY = window.scrollY || window.pageYOffset;
	
	// Aggiungi il listener solo se la pagina è configurata come trasparente
	if (pageHasTransparentNav.value) {
		window.addEventListener('scroll', throttledCheckScrollPosition, { passive: true });
		
		// Controlla la posizione iniziale dopo che il DOM è renderizzato
		nextTick(() => {
			setTimeout(() => {
				checkScrollPosition();
			}, 200);
		});
	}
}, { immediate: true });

// Salva la posizione di scroll PRIMA che menuOpen cambi (per evitare che il padding-top la alteri)
watch(menuOpen, (isOpen, wasOpen) => {
	if (!process.client) return;
	
	// Salva la posizione di scroll PRIMA che il valore cambi
	// Questo watch viene eseguito prima che isTransparentNav cambi
	if (isOpen && !wasOpen) {
		const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
		document.body.setAttribute('data-scroll-y', scrollY.toString());
	}
}, { flush: 'pre' });

// Gestisci il blocco dello scroll del body quando il menu mobile è aperto
watch(menuOpen, (isOpen) => {
	if (!process.client) return;
	
	if (isOpen) {
		// Usa la posizione salvata nel watch precedente
		const scrollY = parseInt(document.body.getAttribute('data-scroll-y') || '0', 10);
		
		// Aspetta che il DOM si aggiorni (il padding-top viene aggiunto quando isTransparentNav diventa false)
		nextTick(() => {
			// Usa doppio requestAnimationFrame per assicurarsi che il DOM sia completamente aggiornato
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					// Blocca lo scroll del body usando la posizione ORIGINALE salvata
					document.body.style.position = 'fixed';
					document.body.style.top = `-${scrollY}px`;
					document.body.style.width = '100%';
					document.body.style.overflow = 'hidden';
					// Previeni il ridimensionamento causato dalla scrollbar
					const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
					if (scrollbarWidth > 0) {
						document.body.style.paddingRight = `${scrollbarWidth}px`;
					}
				});
			});
		});
	} else {
		// Ripristina lo scroll del body
		const scrollY = parseInt(document.body.getAttribute('data-scroll-y') || '0', 10);
		
		// Rimuovi gli stili in nextTick per evitare glitch
		nextTick(() => {
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			document.body.style.overflow = '';
			document.body.style.paddingRight = '';
			document.body.removeAttribute('data-scroll-y');
			
			// Ripristina la posizione di scroll dopo che gli stili sono stati rimossi
			requestAnimationFrame(() => {
				window.scrollTo({
					top: scrollY,
					behavior: 'auto' as ScrollBehavior,
				});
			});
		});
	}
});

// Setup iniziale quando il componente è montato
onMounted(() => {
	if (process.client) {
		lastScrollY = window.scrollY || window.pageYOffset;
		
		// Se siamo su una route non-auth e il fetch non è ancora partito, forzalo
		if (!isAuthRoute.value && status.value === 'idle') {
			refresh();
		}
		
		// Se il fetch è già completato, disattiva il caricamento iniziale (solo al primo caricamento)
		if ((status.value === 'success' || status.value === 'error') && !hasInitialLoadCompleted.value) {
			isInitialLoad.value = false;
			hasInitialLoadCompleted.value = true;
		}
		
		// Timeout di sicurezza: dopo 2 secondi, mostra sempre la navbar anche se il fetch non è completato
		// Questo evita che la navbar rimanga nascosta per sempre in caso di problemi
		// IMPORTANTE: Questo timeout viene eseguito solo al primo caricamento
		if (!hasInitialLoadCompleted.value) {
			setTimeout(() => {
				if (isInitialLoad.value) {
					isInitialLoad.value = false;
					hasInitialLoadCompleted.value = true;
				}
			}, 2000);
		}
		
		if (pageHasTransparentNav.value) {
			window.addEventListener('scroll', throttledCheckScrollPosition, { passive: true });
			nextTick(() => {
				setTimeout(() => {
					checkScrollPosition();
				}, 300);
			});
		}
	}
});

// Cleanup quando il componente viene smontato
onUnmounted(() => {
	if (process.client) {
		window.removeEventListener('scroll', throttledCheckScrollPosition);
		if (rafId !== null) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		// Assicurati di ripristinare lo scroll del body se il menu è ancora aperto
		if (menuOpen.value) {
			const scrollY = parseInt(document.body.getAttribute('data-scroll-y') || '0', 10);
			document.body.style.position = '';
			document.body.style.top = '';
			document.body.style.width = '';
			document.body.style.overflow = '';
			document.body.removeAttribute('data-scroll-y');
			window.scrollTo({
				top: scrollY,
				behavior: 'auto' as ScrollBehavior,
			});
		}
	}
});

const handleLinkClick = (event: MouseEvent | KeyboardEvent) => {
	menuOpen.value = false;
	// Rimuove il focus dopo il click o dopo aver premuto Invio
	// per evitare che l'elemento rimanga evidenziato
	const target = (event.currentTarget || event.target) as HTMLElement | null;
	if (target) {
		// Usa setTimeout per permettere la navigazione prima di rimuovere il focus
		window.setTimeout(() => {
			target.blur();
		}, 0);
	}
};

const handleTriggerClick = (event: MouseEvent) => {
	const target = event.target as HTMLElement | null;
	if (target) {
		window.setTimeout(() => {
			target.blur();
		}, 0);
	}
};

const isActive = (url?: string, permalink?: string) => {
	if (!url && !permalink) return false;
	const linkPath = permalink || url || '';
	const currentPath = route.path;
	return currentPath === linkPath || currentPath.startsWith(linkPath + '/');
};

// Modifica gli elementi della navigation per mostrare "AREA RISERVATA" invece di "Accedi" quando l'utente è autenticato
const navigationItems = computed(() => {
	return props.navigation.items.map(item => {
		// Cerca elementi che puntano a /login o hanno il titolo "Accedi"
		const isLoginItem = item.url === '/login' || 
			item.page?.permalink === '/login' || 
			item.title.toLowerCase() === 'accedi';
		
		// Cerca anche elementi che puntano a /area-riservata o hanno il titolo "AREA RISERVATA"
		const isReservedAreaItem = item.url === '/area-riservata' || 
			item.page?.permalink === '/area-riservata' || 
			item.title.toLowerCase() === 'area riservata';
		
		if (isLoginItem && isAuthenticated.value) {
			// Se l'utente è autenticato, mostra "AREA RISERVATA" invece di "Accedi"
			return {
				...item,
				title: 'AREA RISERVATA',
				url: '/area-riservata',
				page: item.page ? { ...item.page, permalink: '/area-riservata' } : undefined,
			};
		}
		
		if (isReservedAreaItem && !isAuthenticated.value) {
			// Se l'utente non è autenticato, mostra "Accedi" invece di "AREA RISERVATA"
			return {
				...item,
				title: 'Accedi',
				url: '/login',
				page: item.page ? { ...item.page, permalink: '/login' } : undefined,
			};
		}
		
		// Se l'item ha children, controlla anche quelli
		if (item.children) {
			return {
				...item,
				children: item.children.map(child => {
					const isLoginChild = child.url === '/login' || 
						child.page?.permalink === '/login' || 
						child.title.toLowerCase() === 'accedi';
					
					const isReservedAreaChild = child.url === '/area-riservata' || 
						child.page?.permalink === '/area-riservata' || 
						child.title.toLowerCase() === 'area riservata';
					
					if (isLoginChild && isAuthenticated.value) {
						return {
							...child,
							title: 'AREA RISERVATA',
							url: '/area-riservata',
							page: child.page ? { ...child.page, permalink: '/area-riservata' } : undefined,
						};
					}
					
					if (isReservedAreaChild && !isAuthenticated.value) {
						return {
							...child,
							title: 'Accedi',
							url: '/login',
							page: child.page ? { ...child.page, permalink: '/login' } : undefined,
						};
					}
					
					return child;
				}),
			};
		}
		
		return item;
	});
});
</script>

<template>
	<header
		v-if="!isInitialLoad"
		ref="navigationRef"
		:class="[
			'w-full text-foreground z-50 top-0 left-0 right-0 transition-all duration-300',
			isTransparentNav
				? 'absolute bg-transparent shadow-none'
				: 'fixed bg-background',
		]"
		:style="isTransparentNav 
			? 'position: absolute; top: 0; left: 0; right: 0; z-index: 50;'
			: 'position: fixed; top: 0; left: 0; right: 0; z-index: 50;'"
	>
		<Container class="flex items-center justify-between p-4">
			<NuxtLink to="/" class="flex-shrink-0">
				<!-- Quando la navbar è trasparente, mostra sempre il logo dark (se disponibile) -->
				<template v-if="isTransparentNav && darkLogoUrl">
					<img
						:src="darkLogoUrl"
						alt="Logo"
						class="w-[120px] h-auto"
						width="150"
						height="100"
					/>
				</template>
				<!-- Quando la navbar non è trasparente, comportamento normale (light/dark in base al tema) -->
				<template v-else>
					<img :src="lightLogoUrl" alt="Logo" class="w-[120px] h-auto dark:hidden" width="150" height="100" />
					<img
						v-if="darkLogoUrl"
						:src="darkLogoUrl"
						alt="Logo (Dark Mode)"
						class="w-[120px] h-auto hidden dark:block"
						width="150"
						height="100"
					/>
				</template>
			</NuxtLink>

			<nav class="flex items-center gap-4">
				<SearchModal style="display: none;"/>
				<NavigationMenu
					class="hidden md:flex"
					:data-directus="
						setAttr({ collection: 'navigation', item: props.navigation.id, fields: ['items'], mode: 'modal' })
					"
				>
					<NavigationMenuList class="flex gap-6">
						<NavigationMenuItem v-for="section in navigationItems" :key="section.id">
							<template v-if="section.children?.length">
								<NavigationMenuTrigger
									:class="[
										'focus-visible:outline-none font-menu hover:bg-background hover:text-accent !text-lg font-medium',
										isTransparentNav ? '!text-white !bg-transparent' : '!text-black dark:!text-white',
									]"
									@click="handleTriggerClick"
								>
									{{ section.title }}
								</NavigationMenuTrigger>
								<NavigationMenuContent class="min-w-[260px] bg-background p-4 shadow-md">
									<ul class="flex flex-col gap-2">
										<li v-for="child in section.children" :key="child.id">
											<NavigationMenuLink as-child>
												<NuxtLink
													:to="child.page?.permalink || child.url || '#'"
													:class="[
														'font-menu !text-lg font-medium',
														!isTransparentNav ? 'text-black dark:text-white' : 'text-black',
														isActive(child.url, child.page?.permalink) && 'font-bold',
													]"
													@click="handleLinkClick"
													@keydown.enter="handleLinkClick"
												>
													{{ child.title }}
												</NuxtLink>
											</NavigationMenuLink>
										</li>
									</ul>
								</NavigationMenuContent>
							</template>

							<NavigationMenuLink v-else as-child>
								<NuxtLink
									:to="section.page?.permalink || section.url || '#'"
									:class="[
										section.style === 'button'
											? buttonVariants({ variant: 'default', size: 'default' })
											: [
													'font-menu p-2 !text-lg font-medium',
													isTransparentNav ? 'text-white' : 'text-black dark:text-white',
												],
										isActive(section.url, section.page?.permalink) && 'font-bold',
									]"
									@click="handleLinkClick"
									@keydown.enter="handleLinkClick"
								>
									{{ section.title }}
								</NuxtLink>
							</NavigationMenuLink>
						</NavigationMenuItem>
					</NavigationMenuList>
				</NavigationMenu>

				<div class="flex md:hidden">
					<DropdownMenu v-model:open="menuOpen">
						<DropdownMenuTrigger as-child>
							<Button
								variant="link"
								size="icon"
								aria-label="Open menu"
								:class="[
									isTransparentNav
										? 'text-white dark:text-white hover:text-accent dark:hover:text-accent'
										: 'text-black dark:text-white dark:hover:text-accent',
								]"
							>
								<Menu />
							</Button>
						</DropdownMenuTrigger>

						<DropdownMenuContent
							align="start"
							:side-offset="26"
							class="!bg-background w-screen p-6 shadow-md max-w-full overflow-hidden rounded-none"
						>
							<div class="flex flex-col gap-4">
								<div v-for="section in navigationItems" :key="section.id">
									<Collapsible v-if="section.children?.length">
										<CollapsibleTrigger
											:class="[
												'font-menu hover:text-accent w-full text-left flex items-center focus-visible:outline-none !text-lg font-medium',
												!isTransparentNav ? 'text-black dark:text-white' : 'text-black',
											]"
											@click="handleTriggerClick"
										>
											<span>{{ section.title }}</span>
											<ChevronDown class="size-4 ml-1 hover:rotate-180 active:rotate-180 focus:rotate-180" />
										</CollapsibleTrigger>
										<CollapsibleContent class="ml-4 mt-2 flex flex-col gap-2">
											<NuxtLink
												v-for="child in section.children"
												:key="child.id"
												:to="child.page?.permalink || child.url || '#'"
												:class="[
													'font-menu !text-lg font-medium',
													!isTransparentNav ? 'text-black dark:text-white' : 'text-black',
													isActive(child.url, child.page?.permalink) && 'font-bold',
												]"
												@click="handleLinkClick"
												@keydown.enter="handleLinkClick"
											>
												{{ child.title }}
											</NuxtLink>
										</CollapsibleContent>
									</Collapsible>

									<NuxtLink
										v-else
										:to="section.page?.permalink || section.url || '#'"
										:class="[
											section.style === 'button'
												? buttonVariants({ variant: 'default', size: 'default' })
												: [
														'font-menu !text-lg font-medium',
														!isTransparentNav ? 'text-black dark:text-white' : 'text-black',
													],
											isActive(section.url, section.page?.permalink) && 'font-bold',
										]"
										@click="handleLinkClick"
										@keydown.enter="handleLinkClick"
									>
										{{ section.title }}
									</NuxtLink>
								</div>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<ThemeToggle style="display: none;"/>
			</nav>
		</Container>
	</header>
</template>
